import {
  ConfirmModal,
  Loading,
  Table,
  TableRowMenuCell,
  ToggleAllButton,
} from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useExpandedRowKeys } from '@/hooks';
import { useMessage } from '@/providers';
import {
  TABLE_DATETIME_WIDTH,
  TABLE_LONG_CODE_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
  getRecursiveIds,
} from '@/utils';
import dayjs from 'dayjs';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useCreateExpenditureBudgetPurpose,
  useDeleteExpenditureBudgetPurpose,
  useExpenditureBudgetPurposeNestedExpenditurePurpose,
  useUpdateExpenditureBudgetPurpose,
} from '../../hooks';
import {
  type ExpenditureBudgetPurposeFormSchema as ExpenditureBudgetPurposeForMajorMatterFormSchema,
  type ExpenditureBudgetPurposeSearchFormSchema as ExpenditureBudgetPurposeForMajorMatterSearchFormSchema,
  ExpenditurePurposeLevelLabelModifier,
} from '../../models';
import {
  fillZeroToExpenditurePurpose,
  getBudgetPurposeSearchValues,
} from '../../utils';
import { useDrawerState } from '../DrawerStateProvider';
import { ExpenditureItemMajorMatterFormDrawer } from './ExpenditureItemMajorMatterFormDrawer';
import { ExpenditureItemMajorMattersSearchForm } from './ExpenditureItemMajorMattersSearchForm';

export type ExpenditureItemMajorMattersPageForm = z.infer<
  typeof ExpenditureBudgetPurposeForMajorMatterSearchFormSchema
>;

const DEFAULT_PAGE_SIZE = 10;

export const ExpenditureItemMajorMattersPage: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();

  const [searchParams, setSearchParams] = useSearchParams();
  const currentParams: Partial<ExpenditureItemMajorMattersPageForm> & {
    page?: string;
  } = Object.fromEntries(searchParams.entries());

  const editPermission = useMasterTablePermission(true);

  const { mutateAsync: createMutateAsync } =
    useCreateExpenditureBudgetPurpose();
  const { mutateAsync: updateMutateAsync } =
    useUpdateExpenditureBudgetPurpose();
  const { mutateAsync: deleteMutateAsync } =
    useDeleteExpenditureBudgetPurpose();

  const expenditureBudgetPurposes =
    useExpenditureBudgetPurposeNestedExpenditurePurpose({
      enabled:
        !!currentParams.accountingId &&
        !!currentParams.expenditureBudgetJurisdictionId,
      params: {
        type: 'majorMatter',
        page: currentParams.page ?? 1,
        pageSize: DEFAULT_PAGE_SIZE,
        accountingId: currentParams.accountingId ?? '',
        accountTitleId: currentParams.accountTitleId ?? undefined,
        expenditureBudgetJurisdictionId:
          currentParams.expenditureBudgetJurisdictionId ?? '',
        expenditureBudgetOrganizationId:
          currentParams.expenditureBudgetOrganizationId ?? undefined,
      },
    });
  const {
    expandedRowKeys,
    setExpandedRowKeys,
    onExpand,
    clearExpandedRowKeys,
  } = useExpandedRowKeys();
  const [majorMattersDelete, setMajorMattersDelete] = useState<string | null>(
    null,
  );

  const selectedValues = useMemo<
    | Partial<z.infer<typeof ExpenditureBudgetPurposeForMajorMatterFormSchema>>
    | undefined
  >(() => {
    if (
      !drawerState.open ||
      drawerState.type !== 'expenditure-item-major-matter' ||
      !drawerState.isEditing ||
      !drawerState.id
    ) {
      return undefined;
    }
    const parent = expenditureBudgetPurposes.data?.results.find((v) =>
      v.children.some(
        (child) => child.expenditureBudgetPurposeId === drawerState.id,
      ),
    );
    const child = parent?.children.find(
      (v) => v.expenditureBudgetPurposeId === drawerState.id,
    );

    return {
      accountingId: child?.accountingId ?? undefined,
      accountTitleId: child?.accountTitleId ?? undefined,
      jurisdictionId: child?.expenditureBudgetJurisdictionId ?? undefined,
      organizationId: child?.expenditureBudgetOrganizationId ?? undefined,
      expenditurePurposeIds: [parent?.id ?? '', child?.id ?? ''],
      period: [
        child?.expenditureBudgetPurposeStartYear
          ? dayjs().year(child.expenditureBudgetPurposeStartYear)
          : undefined,
        child?.expenditureBudgetPurposeEndYear
          ? dayjs().year(child.expenditureBudgetPurposeEndYear)
          : undefined,
      ],
    };
  }, [drawerState, expenditureBudgetPurposes.data?.results]);

  const data =
    expenditureBudgetPurposes.data?.results.map((v) => ({
      ...v,
      level: 1,
      children: v.children.map((p) => ({ ...p, level: 2, children: null })),
    })) ?? [];

  const openAllExpanded = () => {
    setExpandedRowKeys(
      getRecursiveIds(expenditureBudgetPurposes.data?.results ?? []),
    );
  };

  return (
    <>
      <ExpenditureItemMajorMattersSearchForm
        isSubmitting={expenditureBudgetPurposes.isFetching}
        onSubmit={(value) => {
          setSearchParams(
            {
              page: '1',
              ...getBudgetPurposeSearchValues(value),
            },
            { replace: true },
          );
        }}
      />

      {expenditureBudgetPurposes.isFetching ? (
        <Loading />
      ) : (
        <Table
          dataSource={expenditureBudgetPurposes.isError ? [] : data}
          rowKey="id"
          title={() => (
            <ToggleAllButton
              isExpandedAllWhenMounted
              openAll={openAllExpanded}
              closeAll={clearExpandedRowKeys}
            />
          )}
          expandable={{
            expandedRowKeys,
            onExpand: (expanded, record) => {
              onExpand(expanded, record.id);
            },
          }}
          columns={[
            {
              title: t('code'),
              key: 'code',
              dataIndex: 'code',
              width: TABLE_LONG_CODE_WIDTH,
              render: (value, record) =>
                fillZeroToExpenditurePurpose({
                  value,
                  isLevel2: record.level === 2,
                  isMajorMatter: record.level === 2,
                }),
            },
            {
              title: t('level'),
              key: 'level',
              dataIndex: 'level',
              width: 120,
              render: (value: number) =>
                value === 2
                  ? t('expenditure-purpose-level-3-1')
                  : ExpenditurePurposeLevelLabelModifier[value],
            },
            {
              title: t('expenditure-purpose'),
              key: 'name',
              dataIndex: 'name',
            },
            {
              title: t('start-fiscal-year'),
              key: 'startYear',
              dataIndex: 'expenditureBudgetPurposeStartYear',
              width: TABLE_YEAR_WIDTH,
              onCell: () => ({
                style: { minWidth: TABLE_YEAR_WIDTH },
              }),
            },
            {
              title: t('end-fiscal-year'),
              key: 'endYear',
              dataIndex: 'expenditureBudgetPurposeEndYear',
              width: TABLE_YEAR_WIDTH,
              onCell: () => ({
                style: { minWidth: TABLE_YEAR_WIDTH },
              }),
            },
            {
              title: t('created-datetime'),
              key: 'createdAt',
              dataIndex: 'createdAt',
              width: TABLE_DATETIME_WIDTH,
              render: (value, record) =>
                record.level === 2 ? datetimeFormat({ datetime: value }) : null,
            },
            {
              title: t('updated-datetime'),
              key: 'updatedAt',
              dataIndex: 'updatedAt',
              width: TABLE_DATETIME_WIDTH,
              render: (value, record) =>
                record.level === 2 ? datetimeFormat({ datetime: value }) : null,
            },
            ...(editPermission.data
              ? [
                  {
                    title: '',
                    key: 'actions',
                    fixed: 'right' as const,
                    width: TABLE_ROW_MENU_WIDTH,
                    onCell: () => ({
                      style: { minWidth: TABLE_ROW_MENU_WIDTH },
                    }),
                    render: (
                      value: (typeof data)[0] | (typeof data)[0]['children'][0],
                    ) => {
                      if (
                        !('expenditureBudgetPurposeId' in value) ||
                        !value.expenditureBudgetPurposeId
                      ) {
                        return null;
                      }
                      return (
                        <TableRowMenuCell
                          onEdit={() => {
                            setDrawerState({
                              open: true,
                              type: 'expenditure-item-major-matter',
                              isEditing: true,
                              id: value.expenditureBudgetPurposeId ?? '',
                            });
                          }}
                          items={[
                            {
                              key: 'delete',
                              label: t('delete'),
                              danger: true,
                              onClick: () =>
                                setMajorMattersDelete(
                                  value.expenditureBudgetPurposeId,
                                ),
                            },
                          ]}
                        />
                      );
                    },
                  },
                ]
              : []),
          ]}
          pagination={{
            total: expenditureBudgetPurposes.data?.count,
            current: Number.isNaN(Number(currentParams.page))
              ? 1
              : Number(currentParams.page),
            pageSize: DEFAULT_PAGE_SIZE,
            onChange: (page) => {
              setSearchParams(
                {
                  ...getBudgetPurposeSearchValues(currentParams),
                  page: page.toString(),
                },
                { replace: true },
              );
            },
          }}
        />
      )}

      <ExpenditureItemMajorMatterFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              type: 'edit',
              initialValues: selectedValues,
            }
          : { type: 'new' })}
        onSubmit={async (values) => {
          invariant(drawerState.open, t('unreachable-code'));

          drawerState.isEditing
            ? await updateMutateAsync({ ...values, id: drawerState.id })
            : await createMutateAsync(values);
          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            (async () => {
              setDrawerState({ open: false });
            })(),
          ]);
        }}
      />
      <ConfirmModal
        open={majorMattersDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(majorMattersDelete, t('unreachable-code'));
          try {
            await deleteMutateAsync(majorMattersDelete);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setMajorMattersDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setMajorMattersDelete(null)}
        skipCheck
      />
    </>
  );
};
